import { reactive } from 'vue';
import Http from 'axios';
import Helpers from '@app/global/helpers';
import Builder from '@app/store/builder';
import Navigation from '@app/store/modules/navigation';
import Router from '@app/router';
import RouterFunctions from '@app/router/functions';
import SessionExpired from '@app/exceptions/SessionExpired';
/**
 * Enable/disable debug info in the browser developer console
 */
export const DEBUG_MODE = false;
/**
 * Store root state and type definitions
 */
const STATE = Builder.state({
    pageLoading: window.SSR ? false : true,
    pageData: {},
    locale: null,
    languages: window.LANGUAGES || [],
    routesLoaded: null,
    routesReady: false,
});
/**
 * Store state getters
 */
const GETTERS = {
    activeLanguage() {
        return STATE.languages.find(language => language.locale === Helpers.locale()) || null;
    }
};
/**
 * Store state mutations
 */
const MUTATIONS = {
    setLocale(locale) {
        STATE.locale = locale;
    },
    setLoading(loading) {
        STATE.pageLoading = loading;
    },
    setPageData(path, data) {
        STATE.pageData[path] = data;
    },
    setRoutesLoadedPromise(promise) {
        STATE.routesLoaded = promise;
    },
    setRoutesReady(state) {
        STATE.routesReady = state;
    }
};
/**
 * Store actions
 */
const ACTIONS = {
    async loadRoutesData() {
        const api = Http.get('/api/routes');
        MUTATIONS.setRoutesLoadedPromise(new Promise(resolve => {
            api.then(() => resolve());
        }));
        const response = await api;
        const routes = response.data.map(backendRoute => {
            const route = RouterFunctions.makeRoute(backendRoute);
            // Write eager loaded route data to store
            if (backendRoute.routeable) {
                MUTATIONS.setPageData(route.path, backendRoute.routeable);
            }
            return route;
        }).filter(route => {
            // Filter out already existing routes
            return !RouterFunctions.routeExists(route.path);
        });
        routes.forEach(route => Router.addRoute(route));
        RouterFunctions.addFinalRoutes();
        MUTATIONS.setRoutesReady(true);
        return response;
    },
    loadPageData(path) {
        return Http.get('/api' + path, {
            headers: {
                Accept: 'application/json'
            }
        }).then(res => {
            MUTATIONS.setPageData(path, res.data);
            return res.data;
        }).catch(ex => {
            MUTATIONS.setLoading(false);
            if (ex.response.status === 403) {
                throw new SessionExpired();
            }
            if (ex.response.status === 301 || ex.response.status === 302) {
                MUTATIONS.setPageData(path, ex.response.data);
                return ex.response.data;
            }
        });
    }
};
const Store = reactive({
    STATE,
    GETTERS,
    MUTATIONS,
    ACTIONS,
    Navigation,
    registerModule(name, module) {
        this[name] = module;
    }
});
export default Store;
